import { relations } from 'drizzle-orm'
import {
  decimal,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import { tstzrange } from '../common/customTypes'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getNullableIdReference,
  getTimestampWithTimezone,
} from '../common/utils'
import { feedbacks } from './feedback'
import { maintenanceTasks, maintenanceTaskSteps } from './maintenanceTask'
import { opportunities } from './opportunity'
import { properties } from './property'
import { subscriptions } from './subscription'
import { users } from './user'

export const visitStatusEnum = pgEnum('visit_status', [
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
])

export const visitTaskStatusEnum = pgEnum('visit_task_status', [
  'pending',
  'completed',
  'skipped',
])

export const visitTaskStepStatusEnum = pgEnum('visit_task_step_status', [
  'pending',
  'completed',
  'skipped',
])

export const eventTypeEnum = pgEnum('event_type', [
  'visit',
  'training',
  'meeting',
  'break',
  'survey',
  'other',
  'availability',
])

export const attendeeRoleEnum = pgEnum('attendee_role', [
  'lead',
  'assistant',
  'trainee',
  'instructor',
  'participant',
])

export const attendeeStatusEnum = pgEnum('attendee_status', [
  'confirmed',
  'tentative',
  'declined',
])

export const visits = pgTable(
  'visits',
  {
    ...getBaseFields(),
    subscriptionId: getNullableIdReference(() => subscriptions.id),
    opportunityId: getNullableIdReference(() => opportunities.id),
    propertyId: getNullableIdReference(() => properties.id),
    startTime: getTimestampWithTimezone().notNull(),
    endTime: getTimestampWithTimezone().notNull(),
    eventType: eventTypeEnum().notNull().default('visit'),
    title: text(),
    location: text(),
    latitude: decimal({ precision: 10, scale: 8 }),
    longitude: decimal({ precision: 11, scale: 8 }),
    status: visitStatusEnum().notNull().default('scheduled'),
    startedAt: getTimestampWithTimezone(),
    completedAt: getTimestampWithTimezone(),
    notes: text(),
    internalNotes: text(),
    internalSafetyNotices: text(),
    internalSafetyNoticesReviewedAt: getTimestampWithTimezone(),
    internalSafetyNoticesReviewedBy: getNullableIdReference(() => users.id),
    internalSafetyNoticesReviewComment: text(),
    internalSalesNotes: text(),
  },
  (table) => getBaseIndexes('visits', table),
)

export const visitTasks = pgTable(
  'visit_tasks',
  {
    ...getBaseFields(),
    visitId: getIdReference(() => visits.id),
    maintenanceTaskId: getIdReference(() => maintenanceTasks.id),
    // Snapshot from template
    taskName: text().notNull(),
    taskDescription: text(),
    taskCategory: text(),
    estimatedDurationMinutes: integer(),
    // Completion tracking
    status: visitTaskStatusEnum().notNull().default('pending'),
    completedAt: getTimestampWithTimezone(),
    notes: text(),
    metadata: jsonb(),
  },
  (table) => getBaseIndexes('visit_tasks', table),
)

export const visitTaskSteps = pgTable(
  'visit_task_steps',
  {
    ...getBaseFields(),
    visitTaskId: getIdReference(() => visitTasks.id),
    maintenanceTaskStepId: getIdReference(() => maintenanceTaskSteps.id),
    // Snapshot from maintenanceTaskSteps
    stepNumber: integer().notNull(),
    stepName: text().notNull(),
    stepDescription: text(),
    stepInstruction: text(),
    // Completion tracking
    status: visitTaskStepStatusEnum().notNull().default('pending'),
    completedAt: getTimestampWithTimezone(),
    notes: text(),
  },
  (table) => [
    ...getBaseIndexes('visit_task_steps', table),
    index('visit_task_steps_visit_task_id').on(table.visitTaskId),
  ],
)

export const visitAttendees = pgTable(
  'visit_attendees',
  {
    ...getBaseFields(),
    visitId: getIdReference(() => visits.id),
    userId: getIdReference(() => users.id),
    role: attendeeRoleEnum().notNull().default('participant'),
    status: attendeeStatusEnum().notNull().default('confirmed'),
    notes: text(),
    // Denormalized fields from visits table for constraint enforcement
    // These are automatically populated by database triggers
    visitTimeRange: tstzrange(),
    visitEventType: eventTypeEnum(),
  },
  (table) => getBaseIndexes('visit_attendees', table),
)

export const visitsRelations = relations(visits, ({ one, many }) => ({
  subscription: one(subscriptions, {
    fields: [visits.subscriptionId],
    references: [subscriptions.id],
  }),
  opportunity: one(opportunities, {
    fields: [visits.opportunityId],
    references: [opportunities.id],
  }),
  property: one(properties, {
    fields: [visits.propertyId],
    references: [properties.id],
  }),
  internalSafetyNoticesReviewedByUser: one(users, {
    fields: [visits.internalSafetyNoticesReviewedBy],
    references: [users.id],
  }),
  visitTasks: many(visitTasks),
  visitAttendees: many(visitAttendees),
  feedbacks: many(feedbacks),
}))

export const visitTasksRelations = relations(visitTasks, ({ one, many }) => ({
  visit: one(visits, {
    fields: [visitTasks.visitId],
    references: [visits.id],
  }),
  maintenanceTask: one(maintenanceTasks, {
    fields: [visitTasks.maintenanceTaskId],
    references: [maintenanceTasks.id],
  }),
  steps: many(visitTaskSteps),
}))

export const visitTaskStepsRelations = relations(visitTaskSteps, ({ one }) => ({
  visitTask: one(visitTasks, {
    fields: [visitTaskSteps.visitTaskId],
    references: [visitTasks.id],
  }),
  maintenanceTaskStep: one(maintenanceTaskSteps, {
    fields: [visitTaskSteps.maintenanceTaskStepId],
    references: [maintenanceTaskSteps.id],
  }),
}))

export const visitAttendeesRelations = relations(visitAttendees, ({ one }) => ({
  visit: one(visits, {
    fields: [visitAttendees.visitId],
    references: [visits.id],
  }),
  user: one(users, {
    fields: [visitAttendees.userId],
    references: [users.id],
  }),
}))
